/**
 * The SketchUp Dynamic Components formula language.
 *
 * Measured on the tenant's own files before this was written: 25.547 formulas
 * across 20 SKPs use arithmetic, comparisons, string concatenation and exactly
 * six functions (IF, CONCATENATE, ROUND, AND, FLOOR, OR). The rest of the
 * catalogue below is Excel's vocabulary, supported because SketchUp accepts it
 * and a customer's next file may use it — not because these files do.
 *
 * References come in three shapes, all case-insensitive:
 *   lenx            the component's own attribute
 *   parent!lenx     the attribute of the component that contains it
 *   Divisórias!qtd  the attribute of another component, addressed by name
 *
 * Owner names may contain spaces and accents ("Caixa Inferior!comp_i"), which
 * is why the tokenizer looks ahead for the "!" instead of splitting on spaces.
 */

export type FormulaValue = number | string | boolean;

export class FormulaSyntaxError extends Error {
  constructor(message: string, readonly source: string, readonly position: number) {
    super(message);
    this.name = "FormulaSyntaxError";
  }
}

export class FormulaReferenceError extends Error {
  constructor(readonly reference: string) {
    super(`A fórmula usa ${reference}, que não existe neste componente.`);
    this.name = "FormulaReferenceError";
  }
}

export class FormulaEvaluationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "FormulaEvaluationError";
  }
}

/* -------------------------------------------------------------------------- */
/* Tokenizer                                                                   */
/* -------------------------------------------------------------------------- */

type TokenType =
  | "number"
  | "string"
  | "identifier"
  | "reference"
  | "operator"
  | "open"
  | "close"
  | "comma"
  | "end";

interface Token {
  type: TokenType;
  text: string;
  /** Owner side of a `owner!attribute` reference, already lowercased. */
  owner?: string;
  /** Attribute side of a reference, or the identifier itself, lowercased. */
  name?: string;
  value?: number;
  position: number;
}

/**
 * An owner name accepts letters, digits, underscore, dot, hash and spaces, but
 * never a dash: "A - B!c" must read as a subtraction, not as a component named
 * "A - B". Spaces are only ever consumed when a "!" closes the run.
 */
const REFERENCE_PATTERN = /([\p{L}\p{N}_$#.][\p{L}\p{N}_$#. ]*?)\s*!\s*([\p{L}\p{N}_$#.]+)/uy;
const IDENTIFIER_PATTERN = /[\p{L}_][\p{L}\p{N}_$#.]*/uy;
const NUMBER_PATTERN = /(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?/y;
const TWO_CHARACTER_OPERATORS = new Set(["<=", ">=", "<>"]);
const ONE_CHARACTER_OPERATORS = new Set(["+", "-", "*", "/", "^", "&", "=", "<", ">", "%"]);

export function tokenizeFormula(source: string): Token[] {
  const tokens: Token[] = [];
  let index = 0;
  while (index < source.length) {
    const character = source[index]!;
    if (/\s/.test(character)) {
      index += 1;
      continue;
    }
    if (character === "(") {
      tokens.push({ type: "open", text: "(", position: index });
      index += 1;
      continue;
    }
    if (character === ")") {
      tokens.push({ type: "close", text: ")", position: index });
      index += 1;
      continue;
    }
    if (character === "," || character === ";") {
      tokens.push({ type: "comma", text: ",", position: index });
      index += 1;
      continue;
    }
    if (character === '"' || character === "'") {
      const { text, next } = readString(source, index, character);
      tokens.push({ type: "string", text, position: index });
      index = next;
      continue;
    }
    NUMBER_PATTERN.lastIndex = index;
    const number = NUMBER_PATTERN.exec(source);
    if (number && number.index === index) {
      tokens.push({ type: "number", text: number[0], value: Number(number[0]), position: index });
      index += number[0].length;
      continue;
    }
    REFERENCE_PATTERN.lastIndex = index;
    const reference = REFERENCE_PATTERN.exec(source);
    if (reference && reference.index === index) {
      tokens.push({
        type: "reference",
        text: reference[0],
        owner: reference[1]!.trim().toLocaleLowerCase("en-US"),
        name: reference[2]!.toLocaleLowerCase("en-US"),
        position: index,
      });
      index += reference[0].length;
      continue;
    }
    IDENTIFIER_PATTERN.lastIndex = index;
    const identifier = IDENTIFIER_PATTERN.exec(source);
    if (identifier && identifier.index === index) {
      tokens.push({
        type: "identifier",
        text: identifier[0],
        name: identifier[0].toLocaleLowerCase("en-US"),
        position: index,
      });
      index += identifier[0].length;
      continue;
    }
    const pair = source.slice(index, index + 2);
    if (TWO_CHARACTER_OPERATORS.has(pair)) {
      tokens.push({ type: "operator", text: pair, position: index });
      index += 2;
      continue;
    }
    if (ONE_CHARACTER_OPERATORS.has(character)) {
      tokens.push({ type: "operator", text: character, position: index });
      index += 1;
      continue;
    }
    throw new FormulaSyntaxError(`Caractere inesperado "${character}".`, source, index);
  }
  tokens.push({ type: "end", text: "", position: source.length });
  return tokens;
}

function readString(source: string, start: number, quote: string): { text: string; next: number } {
  let index = start + 1;
  let text = "";
  while (index < source.length) {
    const character = source[index]!;
    if (character === quote) {
      // Excel doubles the quote to escape it.
      if (source[index + 1] === quote) {
        text += quote;
        index += 2;
        continue;
      }
      return { text, next: index + 1 };
    }
    text += character;
    index += 1;
  }
  throw new FormulaSyntaxError("Um texto entre aspas ficou aberto.", source, start);
}

/* -------------------------------------------------------------------------- */
/* Parser                                                                      */
/* -------------------------------------------------------------------------- */

export type FormulaNode =
  | { kind: "literal"; value: FormulaValue }
  | { kind: "self"; name: string }
  | { kind: "parent"; name: string }
  | { kind: "named"; owner: string; name: string }
  | { kind: "unary"; operator: "-" | "+"; operand: FormulaNode }
  | { kind: "binary"; operator: string; left: FormulaNode; right: FormulaNode }
  | { kind: "call"; name: string; args: FormulaNode[] };

/** Token kinds that can begin a value, used to spot a missing operator. */
const STARTS_A_VALUE = new Set<TokenType>(["number", "string", "identifier", "reference", "open"]);

/** Binding power per operator, loosest first. Mirrors Excel's precedence. */
const PRECEDENCE: Record<string, number> = {
  "=": 1, "<>": 1, "<": 1, ">": 1, "<=": 1, ">=": 1,
  "&": 2,
  "+": 3, "-": 3,
  "*": 4, "/": 4,
  "^": 5,
};

export function parseFormula(source: string): FormulaNode {
  const tokens = tokenizeFormula(source);
  let cursor = 0;

  const peek = (): Token => tokens[cursor]!;
  const take = (): Token => tokens[cursor++]!;
  const expect = (type: TokenType, description: string): Token => {
    const token = peek();
    if (token.type !== type) {
      throw new FormulaSyntaxError(`Esperava ${description}.`, source, token.position);
    }
    return take();
  };

  const parseExpression = (minimumPrecedence: number): FormulaNode => {
    let left = parseUnary();
    for (;;) {
      const token = peek();
      let operator: string;
      let precedence: number;
      if (token.type === "operator") {
        const declared = PRECEDENCE[token.text];
        if (declared === undefined || declared < minimumPrecedence) break;
        take();
        operator = token.text;
        precedence = declared;
      } else if (STARTS_A_VALUE.has(token.type)) {
        // Two values side by side with no operator between them. SketchUp
        // accepts it and joins them; these files rely on that, because
        // "Gaveteiro Teste" writes IF(PCA>60,60"") with the comma missing —
        // and rejecting it froze the whole drawer-depth chain.
        precedence = PRECEDENCE["&"]!;
        if (precedence < minimumPrecedence) break;
        operator = "&";
      } else {
        break;
      }
      // "^" is the only right-associative operator here.
      const right = parseExpression(operator === "^" ? precedence : precedence + 1);
      left = { kind: "binary", operator, left, right };
    }
    return left;
  };

  function parseUnary(): FormulaNode {
    const token = peek();
    if (token.type === "operator" && (token.text === "-" || token.text === "+")) {
      take();
      return { kind: "unary", operator: token.text, operand: parseUnary() };
    }
    return parsePrimary();
  }

  /**
   * An argument may be left out entirely, as in `IF(a = 1, b,)`. Excel reads an
   * omitted argument as zero and so does SketchUp — the files use it to mean
   * "and otherwise nothing", which for a position or a length is 0.
   */
  function parseArgument(): FormulaNode {
    const token = peek();
    if (token.type === "comma" || token.type === "close") return { kind: "literal", value: 0 };
    return parseExpression(0);
  }

  function parsePrimary(): FormulaNode {
    const token = take();
    if (token.type === "number") return { kind: "literal", value: token.value! };
    if (token.type === "string") return { kind: "literal", value: token.text };
    if (token.type === "open") {
      const inner = parseExpression(0);
      expect("close", 'um ")"');
      return inner;
    }
    if (token.type === "reference") {
      return token.owner === "parent"
        ? { kind: "parent", name: token.name! }
        : { kind: "named", owner: token.owner!, name: token.name! };
    }
    if (token.type === "identifier") {
      const name = token.name!;
      // SketchUp writes "FLOOR (x)" with a space, so the call is detected by
      // the next token rather than by adjacency.
      if (peek().type === "open") {
        take();
        const args: FormulaNode[] = [];
        if (peek().type !== "close") {
          args.push(parseArgument());
          while (peek().type === "comma") {
            take();
            args.push(parseArgument());
          }
        }
        expect("close", 'um ")" fechando a função');
        return { kind: "call", name, args };
      }
      if (name === "true") return { kind: "literal", value: true };
      if (name === "false") return { kind: "literal", value: false };
      return { kind: "self", name };
    }
    throw new FormulaSyntaxError("Esperava um valor.", source, token.position);
  }

  const parsed = parseExpression(0);
  if (peek().type !== "end") {
    throw new FormulaSyntaxError("Sobrou conteúdo depois do fim da fórmula.", source, peek().position);
  }
  return parsed;
}

/* -------------------------------------------------------------------------- */
/* Evaluator                                                                   */
/* -------------------------------------------------------------------------- */

export interface FormulaContext {
  /** An attribute of the component the formula belongs to. */
  self(name: string): FormulaValue | undefined;
  /** An attribute of the containing component. */
  parent(name: string): FormulaValue | undefined;
  /** An attribute of another component, addressed by name. */
  named(owner: string, name: string): FormulaValue | undefined;
}

export function evaluateFormula(node: FormulaNode, context: FormulaContext): FormulaValue {
  switch (node.kind) {
    case "literal":
      return node.value;
    case "self": {
      const value = context.self(node.name);
      if (value === undefined) throw new FormulaReferenceError(node.name);
      return value;
    }
    case "parent": {
      const value = context.parent(node.name);
      if (value === undefined) throw new FormulaReferenceError(`parent!${node.name}`);
      return value;
    }
    case "named": {
      const value = context.named(node.owner, node.name);
      if (value === undefined) throw new FormulaReferenceError(`${node.owner}!${node.name}`);
      return value;
    }
    case "unary": {
      const operand = toNumber(evaluateFormula(node.operand, context));
      return node.operator === "-" ? -operand : operand;
    }
    case "binary":
      return evaluateBinary(node, context);
    case "call":
      return evaluateCall(node, context);
  }
}

function evaluateBinary(
  node: Extract<FormulaNode, { kind: "binary" }>,
  context: FormulaContext,
): FormulaValue {
  const left = evaluateFormula(node.left, context);
  const right = evaluateFormula(node.right, context);
  switch (node.operator) {
    case "+": return toNumber(left) + toNumber(right);
    case "-": return toNumber(left) - toNumber(right);
    case "*": return toNumber(left) * toNumber(right);
    case "/": {
      const divisor = toNumber(right);
      if (divisor === 0) throw new FormulaEvaluationError("Divisão por zero.");
      return toNumber(left) / divisor;
    }
    case "^": return toNumber(left) ** toNumber(right);
    case "%": {
      const divisor = toNumber(right);
      if (divisor === 0) throw new FormulaEvaluationError("Resto de divisão por zero.");
      return toNumber(left) % divisor;
    }
    case "&": return toText(left) + toText(right);
    default: return compare(node.operator, left, right);
  }
}

/**
 * Two lengths that came through a unit conversion are the same number.
 *
 * SketchUp compares in the unit the component declares, where 55 cm is exactly
 * 55. Here every length is kept in inches, so `LenY-5` on a 600 mm part lands at
 * 55.000000000000004 cm and `PCA<=55` turned false. That single comparison
 * chooses the drawer slide in these files — the runner of a 600 mm cabinet fell
 * off the end of the table and the drawer ran 600 mm instead of 550.
 *
 * The tolerance is relative, so it holds from a 0.8 mm edge band to a 3 m panel,
 * and it is far below anything a millimetre-drawn file can mean.
 */
const COMPARISON_TOLERANCE = 1e-9;

function closeEnough(left: number, right: number): boolean {
  if (left === right) return true;
  const scale = Math.max(Math.abs(left), Math.abs(right));
  return Math.abs(left - right) <= COMPARISON_TOLERANCE * Math.max(1, scale);
}

/**
 * A comparison is numeric whenever both sides read as numbers — the files rely
 * on this, storing `div` as the string "2" and then testing `IF(DIV=1, ...)`.
 * Anything else falls back to a case-insensitive text comparison.
 */
function compare(operator: string, left: FormulaValue, right: FormulaValue): boolean {
  const leftNumber = asNumber(left);
  const rightNumber = asNumber(right);
  if (leftNumber !== undefined && rightNumber !== undefined) {
    const same = closeEnough(leftNumber, rightNumber);
    switch (operator) {
      case "=": return same;
      case "<>": return !same;
      case "<": return !same && leftNumber < rightNumber;
      case ">": return !same && leftNumber > rightNumber;
      case "<=": return same || leftNumber <= rightNumber;
      case ">=": return same || leftNumber >= rightNumber;
    }
  }
  const leftText = toText(left).toLocaleLowerCase("en-US");
  const rightText = toText(right).toLocaleLowerCase("en-US");
  switch (operator) {
    case "=": return leftText === rightText;
    case "<>": return leftText !== rightText;
    case "<": return leftText < rightText;
    case ">": return leftText > rightText;
    case "<=": return leftText <= rightText;
    case ">=": return leftText >= rightText;
    default: throw new FormulaEvaluationError(`Operador desconhecido "${operator}".`);
  }
}

function evaluateCall(
  node: Extract<FormulaNode, { kind: "call" }>,
  context: FormulaContext,
): FormulaValue {
  const evaluateArgument = (index: number): FormulaValue => {
    const argument = node.args[index];
    if (!argument) throw new FormulaEvaluationError(`${node.name.toUpperCase()} exige mais argumentos.`);
    return evaluateFormula(argument, context);
  };
  const numbers = (): number[] => node.args.map((argument) => toNumber(evaluateFormula(argument, context)));

  switch (node.name) {
    // IF is lazy on purpose: the unused branch often divides by an attribute
    // that is zero in the branch that was not taken.
    case "if": {
      const condition = toBoolean(evaluateArgument(0));
      if (condition) return evaluateArgument(1);
      return node.args.length > 2 ? evaluateArgument(2) : false;
    }
    case "and": return node.args.every((argument) => toBoolean(evaluateFormula(argument, context)));
    case "or": return node.args.some((argument) => toBoolean(evaluateFormula(argument, context)));
    case "not": return !toBoolean(evaluateArgument(0));
    case "abs": return Math.abs(toNumber(evaluateArgument(0)));
    case "sqrt": return Math.sqrt(toNumber(evaluateArgument(0)));
    case "int": return Math.floor(toNumber(evaluateArgument(0)));
    case "sign": return Math.sign(toNumber(evaluateArgument(0)));
    case "exp": return Math.exp(toNumber(evaluateArgument(0)));
    case "ln": return Math.log(toNumber(evaluateArgument(0)));
    case "log10": return Math.log10(toNumber(evaluateArgument(0)));
    case "round": return roundTo(toNumber(evaluateArgument(0)), node.args.length > 1 ? toNumber(evaluateArgument(1)) : 0);
    case "roundup": return directedRound(toNumber(evaluateArgument(0)), node.args.length > 1 ? toNumber(evaluateArgument(1)) : 0, "up");
    case "rounddown": return directedRound(toNumber(evaluateArgument(0)), node.args.length > 1 ? toNumber(evaluateArgument(1)) : 0, "down");
    case "floor": return stepRound(toNumber(evaluateArgument(0)), node.args.length > 1 ? toNumber(evaluateArgument(1)) : 1, Math.floor);
    case "ceiling": return stepRound(toNumber(evaluateArgument(0)), node.args.length > 1 ? toNumber(evaluateArgument(1)) : 1, Math.ceil);
    case "mod": {
      const divisor = toNumber(evaluateArgument(1));
      if (divisor === 0) throw new FormulaEvaluationError("MOD com divisor zero.");
      const value = toNumber(evaluateArgument(0));
      return ((value % divisor) + divisor) % divisor;
    }
    case "min": return Math.min(...numbers());
    case "max": return Math.max(...numbers());
    case "sum": return numbers().reduce((total, value) => total + value, 0);
    case "product": return numbers().reduce((total, value) => total * value, 1);
    case "average": {
      const values = numbers();
      if (!values.length) throw new FormulaEvaluationError("AVERAGE sem argumentos.");
      return values.reduce((total, value) => total + value, 0) / values.length;
    }
    case "pi": return Math.PI;
    case "sin": return Math.sin(toNumber(evaluateArgument(0)));
    case "cos": return Math.cos(toNumber(evaluateArgument(0)));
    case "tan": return Math.tan(toNumber(evaluateArgument(0)));
    case "asin": return Math.asin(toNumber(evaluateArgument(0)));
    case "acos": return Math.acos(toNumber(evaluateArgument(0)));
    case "atan": return Math.atan(toNumber(evaluateArgument(0)));
    case "atan2": return Math.atan2(toNumber(evaluateArgument(1)), toNumber(evaluateArgument(0)));
    case "degrees": return (toNumber(evaluateArgument(0)) * 180) / Math.PI;
    case "radians": return (toNumber(evaluateArgument(0)) * Math.PI) / 180;
    case "concatenate": return node.args.map((argument) => toText(evaluateFormula(argument, context))).join("");
    case "len": return toText(evaluateArgument(0)).length;
    case "left": return toText(evaluateArgument(0)).slice(0, node.args.length > 1 ? toNumber(evaluateArgument(1)) : 1);
    case "right": {
      const text = toText(evaluateArgument(0));
      const count = node.args.length > 1 ? toNumber(evaluateArgument(1)) : 1;
      return count <= 0 ? "" : text.slice(-count);
    }
    case "mid": {
      const text = toText(evaluateArgument(0));
      const start = Math.max(1, toNumber(evaluateArgument(1)));
      return text.slice(start - 1, start - 1 + toNumber(evaluateArgument(2)));
    }
    case "upper": return toText(evaluateArgument(0)).toLocaleUpperCase("pt-BR");
    case "lower": return toText(evaluateArgument(0)).toLocaleLowerCase("pt-BR");
    case "trim": return toText(evaluateArgument(0)).trim();
    case "value": return toNumber(evaluateArgument(0));
    case "text": return toText(evaluateArgument(0));
    case "choose": {
      const index = Math.round(toNumber(evaluateArgument(0)));
      if (index < 1 || index >= node.args.length) {
        throw new FormulaEvaluationError(`CHOOSE recebeu o índice ${index}, fora da lista.`);
      }
      return evaluateArgument(index);
    }
    default:
      throw new FormulaEvaluationError(`A função ${node.name.toUpperCase()} ainda não é suportada.`);
  }
}

function roundTo(value: number, digits: number): number {
  const factor = 10 ** Math.round(digits);
  return Math.round(value * factor) / factor;
}

function directedRound(value: number, digits: number, direction: "up" | "down"): number {
  const factor = 10 ** Math.round(digits);
  const scaled = value * factor;
  const rounded = direction === "up"
    ? (scaled >= 0 ? Math.ceil(scaled) : Math.floor(scaled))
    : (scaled >= 0 ? Math.floor(scaled) : Math.ceil(scaled));
  return rounded / factor;
}

function stepRound(value: number, step: number, round: (value: number) => number): number {
  if (step === 0) return 0;
  return round(value / step) * step;
}

/* -------------------------------------------------------------------------- */
/* Coercions                                                                   */
/* -------------------------------------------------------------------------- */

/** The numeric reading of a value, or undefined when it simply is not a number. */
export function asNumber(value: FormulaValue): number | undefined {
  if (typeof value === "number") return Number.isFinite(value) ? value : undefined;
  if (typeof value === "boolean") return value ? 1 : 0;
  const text = value.trim();
  if (!text) return undefined;
  const parsed = Number(text.replace(",", "."));
  return Number.isFinite(parsed) ? parsed : undefined;
}

export function toNumber(value: FormulaValue): number {
  const parsed = asNumber(value);
  if (parsed === undefined) {
    throw new FormulaEvaluationError(`"${String(value)}" não é um número.`);
  }
  return parsed;
}

export function toText(value: FormulaValue): string {
  if (typeof value === "string") return value;
  if (typeof value === "boolean") return value ? "TRUE" : "FALSE";
  return formatNumber(value);
}

export function toBoolean(value: FormulaValue): boolean {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  const text = value.trim().toLocaleLowerCase("en-US");
  if (text === "true") return true;
  if (text === "false" || text === "") return false;
  const parsed = asNumber(value);
  return parsed === undefined ? true : parsed !== 0;
}

/** Drops the floating-point tail so "3" never reads as "3.0000000000000004". */
function formatNumber(value: number): string {
  if (Number.isInteger(value)) return String(value);
  return String(Number(value.toPrecision(12)));
}
